import { FontAwesomeIcon } from 'components/ui/FontAwesomeIcon';
import * as React from 'react';
import { Button, CardBody, CardFooter, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { HttpRequest } from 'util/http';
import { HierarchySelect } from '../../hierarchies/HierarchySelect';
import { AvidSelect } from '../AvidSelect';
import { IAvid } from '../interfaces';

interface IAvidEditorProps {
  editMode?: IAvid
  onSuccess?: () => Promise<any> | void
  onCancel?: () => void
  className?: string
}

interface IAvidEditorState {
  avid: string
  clientsid: string
  label: string
  parentid: string
  hierarchylevel: string
  dynamicChildren: number
  childInputIdField: string
  childLabelIdField: string
  isSubmitting: boolean
}

export class AvidEditor extends React.Component<IAvidEditorProps, IAvidEditorState> {
  initState: IAvidEditorState = {
    avid: '',
    clientsid: '',
    label: '',
    parentid: '',
    hierarchylevel: '',
    dynamicChildren: 0,
    childInputIdField: '',
    childLabelIdField: '',
    isSubmitting: false,
  }

  state: IAvidEditorState = { ...this.initState };

  componentWillMount() {
    const { editMode } = this.props

    if (editMode) {
      const { avid, clientsid, label, parentid, hierarchylevel, dynamicChildren } = editMode
      let { childInputIdField, childLabelIdField } = editMode
      childInputIdField = childInputIdField || ''
      childLabelIdField = childLabelIdField || ''

      this.setState({ avid, clientsid, label, parentid, hierarchylevel, childInputIdField, childLabelIdField, dynamicChildren });
    }
  }

  submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    const method = 'POST'
    const { onSuccess } = this.props
    const { avid, clientsid: cientid, label, parentid, hierarchylevel } = this.state
    const { dynamicChildren, childLabelIdField: childLabel, childInputIdField: childInput } = this.state
    const endpoint = `/scripts/avids/avids_${this.isEditMode() ? 'modify' : 'add'}`
    const data: any = { avid, cientid, label, dynamicChildren, childLabel, childInput };

    if (parentid) {
      data.parentid = parentid
    }

    if (hierarchylevel) {
      data.hierarchylevel = hierarchylevel
    }

    this.setState({ isSubmitting: true })

    HttpRequest.fetch(endpoint, { data, method })
      .then(() => {
        let onSuccessPromise = onSuccess()

        if (!onSuccessPromise) {
          onSuccessPromise = Promise.resolve()
        }

        return onSuccessPromise.then(() => this.resetToInitState())
      })
      .finally(() => this.setState({ isSubmitting: false }))
  }

  resetToInitState() {
    this.setState({ ...this.initState })
  }

  isEditMode() {
    return !!this.props.editMode
  }

  render() {
    const { dynamicChildren, isSubmitting } = this.state
    const { className, onCancel = () => ({}) } = this.props
    const isDynamicChildren = dynamicChildren === 1

    return (
      <Form onSubmit={(e) => this.submit(e)} className={className}>
        <CardBody className="bg-white">
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Avid</Label>
                <Input type="text" required
                  readOnly={isSubmitting}
                  disabled={this.isEditMode()}
                  onChange={(e) => this.setState({ avid: e.target.value })}
                  value={this.state.avid} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Clients Id</Label>
                <Input type="text" required
                  readOnly={isSubmitting}
                  onChange={(e) => this.setState({ clientsid: e.target.value })}
                  value={this.state.clientsid} />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Label</Label>
                <Input type="text" required
                  readOnly={isSubmitting}
                  onChange={(e) => this.setState({ label: e.target.value })}
                  value={this.state.label} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Parent Avid</Label>
                <AvidSelect
                  readOnly={isSubmitting}
                  onChange={(parentid) => this.setState({ parentid })}
                  includeRootElement={true}
                  value={this.state.parentid} />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Hierarchy</Label>
                <HierarchySelect
                  readOnly={isSubmitting}
                  onChange={(hierarchylevel) => this.setState({ hierarchylevel })}
                  value={this.state.hierarchylevel} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <Label>Children Type</Label>
              <FormGroup>
                <FormGroup check inline>
                  <Input type="radio" name="childrenType" id="childrenTypeClassic"
                    onChange={() => this.setState({ dynamicChildren: 0 })}
                    checked={!isDynamicChildren}
                    readOnly={isSubmitting}
                    value={0} />
                  <Label check htmlFor="childrenTypeClassic">Classic Children</Label>
                </FormGroup>
                <FormGroup check inline>
                  <Input type="radio" name="childrenType" id="childrenTypeDynamic"
                    onChange={() => this.setState({ dynamicChildren: 1 })}
                    checked={isDynamicChildren}
                    readOnly={isSubmitting}
                    value={1} />
                  <Label check htmlFor="childrenTypeDynamic">Dynamic Children</Label>
                </FormGroup>
              </FormGroup>
            </Col>
          </Row>

          {isDynamicChildren &&
            <Row>
              <Col md={6}>
                <Label>Child Input Id Field</Label>
                <Input type="text" placeholder="e.g. ColumnHeaderName"
                  onChange={(e) => this.setState({ childInputIdField: e.target.value })}
                  value={this.state.childInputIdField}
                  readOnly={isSubmitting}
                  required={isDynamicChildren} />
              </Col>
              <Col md={6}>
                <Label>Child Label Id Field</Label>
                <Input type="text" placeholder="e.g. ColumnHeaderName"
                  onChange={(e) => this.setState({ childLabelIdField: e.target.value })}
                  value={this.state.childLabelIdField}
                  readOnly={isSubmitting}
                  required={isDynamicChildren} />
              </Col>
            </Row>
          }
        </CardBody>
        <CardFooter className="d-flex justify-content-end">
          <Button color="secondary" className="mr-2" type="submit"
            readOnly={isSubmitting}>
            <span>Submit&nbsp;</span>
            {isSubmitting &&
              <FontAwesomeIcon icon="cog" spin />
            }
          </Button>
          <Button color="light"
            onClick={() => onCancel()}
            readOnly={isSubmitting}>
            <span>Cancel</span>
          </Button>
        </CardFooter>
      </Form>
    )
  }
}
